(() => {
  'use strict';
  const configElement = document.getElementById('pw-behavior-config');
  if (!configElement || document.documentElement.dataset.pwBehaviorLoaded) return;
  document.documentElement.dataset.pwBehaviorLoaded = 'true';
  const config = JSON.parse(configElement.textContent);
  const debugOption = new URLSearchParams(location.search).get('pw_analytics_debug');
  const debug = debugOption === 'local' || debugOption === 'send';
  const mode = debug ? 'qa' : 'live';
  const storageKey = `pw_journey_v1_${mode}`;
  const sectionEvents = {
    shipping_returns: 'pw_shipping_returns_open',
    materials_care: 'pw_materials_care_open',
    color_texture: 'pw_color_texture_open',
    size_guide: 'pw_size_guide_open'
  };
  const sections = new Map();
  const seenSections = new Set();
  const seenDepths = new Set();
  let lastSection = 'none';
  let lastAction = 'page_arrive';
  let lastTarget = '';
  let navigationAt = 0;
  let arrivalSent = false;
  let activeSince = document.visibilityState === 'visible' ? performance.now() : null;
  let activeMs = 0;
  let panel;
  let log;

  function publicPath(input) {
    try {
      const url = new URL(input, location.origin);
      if (url.origin !== location.origin) return '';
      const path = url.pathname;
      if (/^\/(?:products|collections)\/[a-z0-9-]+\/?$/i.test(path)) return path;
      if (/^\/policies\/(?:shipping-policy|refund-policy|privacy-policy|terms-of-service|contact-information)\/?$/.test(path)) return path;
      if (/^\/pages\/(?:contact|returns-quality|data-sharing-opt-out|about|our-story)\/?$/.test(path)) return path;
      if (/^\/(?:cart|search|collections|products)?\/?$/.test(path)) return path;
      if (/^\/checkouts?\//.test(path) || path === '/checkout') return '/checkout';
      return '';
    } catch (_) {
      return '';
    }
  }

  const pagePath = publicPath(location.href);
  if (!pagePath) return;

  function permission() {
    const privacy = window.Shopify?.customerPrivacy;
    let allowed = false;
    let country = '';
    try {
      allowed = privacy?.analyticsProcessingAllowed() === true;
      country = String(privacy?.getRegion() || '').slice(0, 2).toUpperCase();
    } catch (_) {}
    return { allowed, country, eligible: allowed && (country === 'US' || debug) && ((!config.designMode && config.liveTheme) || debug) };
  }

  function showStatus(message) {
    if (!debug || !document.body) return;
    if (!panel) {
      panel = document.createElement('aside');
      panel.id = 'pw-analytics-debug';
      panel.setAttribute('aria-label', 'Analytics QA');
      panel.style.cssText = 'position:fixed;right:8px;bottom:8px;z-index:2147483647;max-width:440px;max-height:32vh;overflow:auto;padding:12px;background:#fff;color:#111;border:2px solid #333;font:12px/1.4 monospace;white-space:pre-wrap;box-shadow:0 2px 8px #0003';
      const title = document.createElement('strong');
      title.textContent = 'Analytics QA';
      panel.append(title, document.createElement('div'), document.createElement('pre'));
      log = panel.querySelector('pre');
      document.body.append(panel);
    }
    panel.querySelector('div').textContent = message;
  }

  function transport() {
    if (typeof window.gtag === 'function') return window.gtag;
    if (Array.isArray(window.dataLayer)) return function () { window.dataLayer.push(arguments); };
    return null;
  }

  function persist() {
    if (!permission().eligible) return;
    try {
      sessionStorage.setItem(storageKey, JSON.stringify({ path: pagePath, section: lastSection, action: lastAction, at: Date.now() }));
    } catch (_) {}
  }

  function emit(name, parameters = {}) {
    const state = permission();
    const send = transport();
    if (!state.eligible || (!send && debugOption !== 'local')) {
      showStatus(`Not sending. Country: ${state.country || 'unknown'}. Analytics consent: ${state.allowed}. Existing Google tag: ${Boolean(send)}.`);
      return false;
    }
    const payload = {
      send_to: config.measurementId,
      pw_mode: mode,
      pw_page: pagePath,
      pw_product: config.product || 'none',
      pw_last_section: lastSection,
      pw_last_action: lastAction,
      pw_visible_ms: Array.from(sections.values()).filter(state => state.name === lastSection).reduce((total, state) => total + sectionTime(state), 0),
      page_location: location.origin + pagePath,
      ...parameters
    };
    if (debug) payload.debug_mode = true;
    if (debugOption !== 'local') send('event', name, payload);
    showStatus(`Country: ${state.country || 'unknown'}. Analytics consent: ${state.allowed}. Mode: ${debugOption}.`);
    if (log) log.textContent = `${name} ${JSON.stringify(payload)}\n${log.textContent}`.slice(0, 10000);
    return true;
  }

  function action(name, parameters = {}) {
    if (emit(name, parameters)) {
      lastAction = name;
      persist();
    }
  }

  function activeTime() {
    return Math.round(activeMs + (activeSince === null ? 0 : performance.now() - activeSince));
  }

  function sectionTime(state) {
    return Math.round(state.visibleMs + (state.since === null ? 0 : performance.now() - state.since));
  }

  function updateSectionVisibility() {
    const now = performance.now();
    for (const [details, state] of sections) {
      const summaryRect = details.querySelector('summary').getBoundingClientRect();
      const summaryVisible = document.visibilityState === 'visible' && summaryRect.bottom > 0 && summaryRect.top < innerHeight;
      if (summaryVisible && !seenSections.has(state.name) && emit('pw_section_visible', { pw_section: state.name })) seenSections.add(state.name);
      const content = details.querySelector('summary').nextElementSibling;
      const rect = content?.getBoundingClientRect();
      const visible = details.open && document.visibilityState === 'visible' && rect && rect.height > 0 && rect.bottom > 0 && rect.top < innerHeight;
      if (visible && state.since === null) state.since = now;
      if (!visible && state.since !== null) {
        state.visibleMs += now - state.since;
        state.since = null;
      }
    }
  }

  function area(element) {
    if (element.closest('.aw-product-details')) return 'product_details';
    if (element.closest('.aw-size-guide')) return 'size_guide';
    if (element.closest('aw-sticky-buy')) return 'sticky_buy';
    if (element.closest('product-recommendations')) return 'recommendations';
    if (element.closest('.aw-header')) return 'header';
    if (element.closest('.aw-footer')) return 'footer';
    if (element.closest('.pw-newsletter')) return 'newsletter';
    if (element.closest('[id^="ProductInfo-"]')) return 'product';
    if (element.closest('cart-drawer, cart-items, #main-cart-footer')) return 'cart';
    return 'page_content';
  }

  function arrive() {
    if (arrivalSent || !permission().eligible) return;
    let previous = {};
    try {
      previous = JSON.parse(sessionStorage.getItem(storageKey) || '{}');
      if (!previous.at || Date.now() - previous.at > 1800000) previous = {};
    } catch (_) {}
    if (previous.section && Object.hasOwn(sectionEvents, previous.section)) lastSection = previous.section;
    if (emit('pw_page_arrive', { pw_previous_page: previous.path ? publicPath(previous.path) || 'none' : 'none', pw_previous_action: previous.action || 'none' })) {
      arrivalSent = true;
      persist();
      updateSectionVisibility();
      const success = document.querySelector('.pw-newsletter__message:not(.pw-newsletter__message--error)');
      if (success) action('pw_newsletter_success', { pw_area: 'newsletter' });
    }
  }

  function initialize() {
    document.querySelectorAll('details[data-pw-section]').forEach(details => {
      const name = details.dataset.pwSection;
      if (!Object.hasOwn(sectionEvents, name)) return;
      const state = { name, visibleMs: 0, since: null, open: details.open };
      sections.set(details, state);
      details.addEventListener('toggle', () => {
        if (details.open === state.open) return;
        state.open = details.open;
        updateSectionVisibility();
        if (details.open) {
          state.visibleMs = 0;
          state.since = null;
          if (permission().eligible) lastSection = name;
          action(sectionEvents[name], { pw_section: name, pw_area: name === 'size_guide' ? 'size_guide' : 'product_details' });
          updateSectionVisibility();
        } else {
          action('pw_section_close', { pw_section: name, pw_visible_ms: sectionTime(state) });
        }
      });
    });

    document.addEventListener('click', event => {
      if (!event.isTrusted || !(event.target instanceof Element)) return;
      const target = event.target;
      const button = target.closest('button');
      if (button?.matches('.product-form__submit, aw-sticky-buy button')) action('pw_buy_click', { pw_area: area(button) });
      else if (button?.matches('[name="checkout"], .cart__checkout-button')) action('pw_checkout_click', { pw_area: 'cart' });
      else if (button?.matches('.thumbnail')) {
        const buttons = Array.from(button.closest('media-gallery')?.querySelectorAll('.thumbnail') || []);
        action('pw_gallery_select', { pw_value: String(buttons.indexOf(button) + 1), pw_area: 'gallery' });
      } else if (button?.matches('.product__media-toggle')) action('pw_gallery_zoom', { pw_area: 'gallery' });

      const anchor = target.closest('a[href]');
      if (!anchor) return;
      const href = anchor.getAttribute('href');
      if (anchor.id === 'more-payment-options-link') {
        action('pw_payment_options_click', { pw_area: 'product' });
        return;
      }
      if (href.startsWith('mailto:') || href.startsWith('tel:')) {
        action('pw_contact_click', { pw_area: area(anchor), pw_value: href.startsWith('mailto:') ? 'email' : 'phone' });
        return;
      }
      const path = publicPath(anchor.href);
      if (!path) return;
      const samePage = path === pagePath && new URL(anchor.href).hash;
      action(samePage ? 'pw_anchor_link' : 'pw_internal_link', { pw_target: path, pw_area: area(anchor) });
      if (!samePage && anchor.target !== '_blank' && !event.ctrlKey && !event.metaKey && !event.shiftKey && !event.altKey) {
        lastTarget = path;
        navigationAt = performance.now();
      }
    }, true);

    document.addEventListener('change', event => {
      const input = event.target;
      if (!(input instanceof Element) || !input.matches('variant-selects input[type="radio"], variant-selects select')) return;
      const value = String(input.value || '').trim();
      if (/^\d{2,3}\s*[×x]\s*\d{2,3}(?:\s*in)?$/.test(value)) action('pw_size_select', { pw_value: value, pw_area: 'product' });
    });

    document.addEventListener('submit', event => {
      const form = event.target;
      if (!(form instanceof Element)) return;
      if (form.matches('.pw-newsletter__form')) action('pw_newsletter_submit_attempt', { pw_area: 'newsletter' });
      else if (form.matches('[role="search"]')) action('pw_search_submit', { pw_area: 'header' });
    }, true);

    document.querySelectorAll('.aw-search, .aw-mobile-menu, .aw-collection-menu').forEach(details => {
      details.addEventListener('toggle', () => {
        if (details.open) action(details.matches('.aw-search') ? 'pw_search_open' : 'pw_navigation_open', { pw_area: 'header' });
      });
    });

    const errorWrapper = document.querySelector('.product-form__error-message-wrapper');
    if (errorWrapper) {
      let errorVisible = !errorWrapper.hidden;
      new MutationObserver(() => {
        const visible = !errorWrapper.hidden && errorWrapper.textContent.trim().length > 0;
        if (visible && !errorVisible) action('pw_cart_error', { pw_area: 'product' });
        errorVisible = visible;
      }).observe(errorWrapper, { attributes: true, childList: true, subtree: true });
    }

    let scrollQueued = false;
    document.addEventListener('scroll', () => {
      if (scrollQueued) return;
      scrollQueued = true;
      requestAnimationFrame(() => {
        scrollQueued = false;
        updateSectionVisibility();
        if (document.visibilityState !== 'visible') return;
        const depth = (scrollY + innerHeight) / Math.max(document.documentElement.scrollHeight, innerHeight) * 100;
        for (const threshold of [25, 50, 75, 90]) {
          if (depth >= threshold && !seenDepths.has(threshold) && emit('pw_scroll_depth', { pw_value: String(threshold) })) seenDepths.add(threshold);
        }
      });
    }, { passive: true });

    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        if (activeSince !== null) activeMs += performance.now() - activeSince;
        activeSince = null;
        updateSectionVisibility();
        emit('pw_page_pause', { pw_active_ms: activeTime() });
        persist();
      } else {
        activeSince = performance.now();
        updateSectionVisibility();
      }
    });

    window.addEventListener('pagehide', event => {
      emit('pw_page_leave', { pw_target: performance.now() - navigationAt < 5000 ? lastTarget : '', pw_value: event.persisted ? 'bfcache' : 'document_left', pw_active_ms: activeTime() });
      persist();
    });
    window.addEventListener('pageshow', event => {
      if (event.persisted) {
        lastTarget = '';
        navigationAt = 0;
        action('pw_page_resume');
      }
    });

    document.addEventListener('visitorConsentCollected', () => {
      if (!permission().allowed) {
        try { sessionStorage.removeItem(storageKey); } catch (_) {}
        lastSection = 'none';
        lastAction = 'page_arrive';
      }
      arrive();
    });

    arrive();
    if (window.Shopify?.loadFeatures) {
      window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.2' }], () => arrive());
    }
    let attempts = 0;
    const ready = setInterval(() => {
      arrive();
      if (arrivalSent || ++attempts >= 40) {
        clearInterval(ready);
        const state = permission();
        showStatus(`Country: ${state.country || 'unknown'}. Analytics consent: ${state.allowed}. Ready: ${arrivalSent}. Mode: ${debugOption}.`);
      }
    }, 250);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize, { once: true });
  else initialize();
})();
